import { diseaseNameToId } from 'constants/diseases'

import { useState } from 'react'
import T from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { get, set } from 'lodash'
import Button from 'components/Button'
import Input from 'components/Input'
import Split from 'components/Split'
import Info from 'components/Info'
import Accordion from 'components/Accordion'
import Message from 'components/Message'
import { isValidNationalId } from 'lib/birthNumber'
import useSession from 'hooks/useSession'
import Joi from 'joi'

import Step from '../../components/Step'

export const validator = (values, user_id) =>
  Joi.object({
    details: Joi.object({
      on_behalf_of: Joi.object({
        national_id: Joi.string().required(),
        first_name: Joi.string().required(),
        last_name: Joi.string().required(),
        relation: Joi.string().required(),
      }).allow(null),
    })
      .unknown(true)
      .required(),
  }).unknown(true)

export default function SelectPatient({ changeStep, values, onChange, orgUnit, bookingType }) {
  const { push } = useHistory()
  const { session } = useSession()
  const [expanded, setExpanded] = useState(
    typeof get(values, 'details.on_behalf_of') === 'object' &&
      Object.keys(get(values, 'details.on_behalf_of') || {}).length > 0,
  )
  const [errors, setErrors] = useState(null)

  const message = get(orgUnit, `messages.${bookingType.id}`, null) || null

  const name = `${get(session, 'first_name', '')} ${get(session, 'last_name', '')}`
  const bookingTypeSlug = get(bookingType, 'slug', '–') || '–'

  const handleToggle = (value) => {
    if (value) {
      onChange('details.on_behalf_of', {})
    }

    setExpanded(value)
  }

  const handleChange = (key) => ({ target: { value } }) => onChange(key, value || undefined)

  const checkErrors = () => {
    const errors = {}

    if (!get(values, 'details.on_behalf_of.national_id')) {
      set(errors, 'details.on_behalf_of.national_id', 'Dette feltet er påkrevd.')
    } else if (!isValidNationalId(get(values, 'details.on_behalf_of.national_id'))) {
      set(
        errors,
        'details.on_behalf_of.national_id',
        'Et gyldig fødselsnummer må ha 11 siffer uten mellomrom og oppfyll kravene til norske D-nummer eller H-nummer.',
      )
    } else if (get(values, 'details.on_behalf_of.national_id') === get(session, 'user_id')) {
      set(
        errors,
        'details.on_behalf_of.national_id',
        `Om du skal rapportere for deg selv klikker du på «${name} (deg)» ovenfor.`,
      )
    }

    if (!get(values, 'details.on_behalf_of.first_name')) {
      set(errors, 'details.on_behalf_of.first_name', 'Dette feltet er påkrevd.')
    }

    if (!get(values, 'details.on_behalf_of.last_name')) {
      set(errors, 'details.on_behalf_of.last_name', 'Dette feltet er påkrevd.')
    }

    if (!get(values, 'details.on_behalf_of.relation')) {
      set(errors, 'details.on_behalf_of.relation', 'Dette feltet er påkrevd.')
    }

    return errors
  }

  // Only do this for covid
  const checkBookingPermitted = () => {
    if (bookingType?.id !== 'COVID_VACCINE') {
      return true
    }

    // check for eligibility, wait list etc. here before moving on
    const diseaseId = diseaseNameToId('covid19')
    const bookingNotPermitted =
      orgUnit?.vaccination_properties?.[diseaseId]?.disable_waiting_list &&
      !values?.isEligible &&
      !values?.on_behalf_of?.national_id

    if (bookingNotPermitted) {
      push(`/${orgUnit.slug}/utilgjengelig`)
      return false
    }

    return true
  }

  const handleSelect = () => {
    onChange('details.on_behalf_of', null)

    // don't change step if booking is blocked
    if (!checkBookingPermitted()) {
      return
    }

    changeStep()
  }

  const handleSubmit = () => {
    const errors = checkErrors()

    if (Object.keys(get(errors, 'details.on_behalf_of', {})).length) {
      setErrors(errors)
      return
    }

    // don't change step if booking is blocked
    if (!checkBookingPermitted()) {
      return
    }

    // Remove vaccinationId if set when an "on behalf of" booking
    // This fixes a corner case where the user might have selected an active vacination,
    // and then return to the beginning to create an "on behalf of" booking.
    // This would cause the user to enter the wrong flow (update existing vaccination)
    onChange('vaccinationId', null)

    setErrors(null)
    changeStep()
  }

  const selectedSelf = get(values, 'details.on_behalf_of', false) === null

  return (
    <Step
      title={`Hvem bestiller du ${bookingTypeSlug} for?`}
      onSubmit={handleSubmit}
      submit={expanded ? 'Gå videre' : undefined}
    >
      {message && <Message type="notice">{message}</Message>}

      {bookingTypeSlug === 'koronavaksine' && (
        <Message type="notice">
          Husk at du selv er ansvarlig for riktig intervall mellom vaksinene. Dersom du, eller den
          den du bestiller for, ikke har rett til vaksine, kan man bli avvist på vaksinasjonsstedet.
        </Message>
      )}

      <Button arrow primary={selectedSelf && !expanded} full spaceless onClick={handleSelect}>
        Meg selv ({name || 'mangler navn'})
      </Button>

      <Accordion
        title="En annen person"
        full
        rounded
        onToggle={handleToggle}
        defaultExpanded={expanded}
      >
        <Info margins type="light">
          Du har mulighet til å bestille time for {bookingTypeSlug} til personer du har en nær
          relasjon til, for eksempel eget barn eller forelder.
        </Info>

        <Input
          short
          label="Fødselsnummer til personen"
          placeholder="11 siffer"
          inputMode="numeric"
          pattern="[0-9]*"
          value={get(values, 'details.on_behalf_of.national_id', '')}
          onChange={handleChange('details.on_behalf_of.national_id')}
          error={get(errors, 'details.on_behalf_of.national_id')}
        />
        <Split>
          <Input
            label="Fornavn"
            value={get(values, 'details.on_behalf_of.first_name', '')}
            onChange={handleChange('details.on_behalf_of.first_name')}
            error={get(errors, 'details.on_behalf_of.first_name')}
          />

          <Input
            label="Etternavn"
            value={get(values, 'details.on_behalf_of.last_name', '')}
            onChange={handleChange('details.on_behalf_of.last_name')}
            error={get(errors, 'details.on_behalf_of.last_name')}
          />
        </Split>
        <Input
          label="Hva er din relasjon til personen?"
          value={get(values, 'details.on_behalf_of.relation', '')}
          onChange={handleChange('details.on_behalf_of.relation')}
          error={get(errors, 'details.on_behalf_of.relation')}
          placeholder="Forelder, verge, datter el."
          maxLength="40"
        />
      </Accordion>

      <Info margins type="light">
        Informasjon du oppgir vil bli behandlet trygt og kun brukt i forbindelse med din{' '}
        {bookingTypeSlug}. Les mer om hvordan vi tar vare på ditt{' '}
        <Link to="/personvern">personvern</Link>.
      </Info>
    </Step>
  )
}

SelectPatient.propTypes = {
  changeStep: T.func.isRequired,
  values: T.object.isRequired,
  bookingType: T.object.isRequired,
  onChange: T.func.isRequired,
  orgUnit: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
    vaccination_properties: T.object.isRequired,
  }).isRequired,
}
